import {
  Award,
  HeartHandshake,
  ClipboardCheck,
  BookOpen,
  ClipboardPen,
  CreditCard,
  Flag,
  LayoutList,
  Library,
  Contact,
  Gift,
  House,
  Inbox,
  MapPin,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Settings,
  Trophy,
  UserCog,
  Building2,
} from "lucide-react";
import type { NavItem, NavSection } from "@skypointcloud/skypoint-ui";
import type { EmpCapability } from "../../../../shared/employee/access";

export const EMPLOYEE_NAV_IDS = {
  HOME: "emp-home",
  /** Users & access — the shared /users page (shared user management). */
  ADMIN_USERS: "emp-admin-users",
  /** Locations and teams (the page that used to be "Users & organisation"). */
  ADMIN_ORGANISATION: "emp-admin-organisation",
  ADMIN_SETTINGS: "emp-admin-settings",
  ADMIN_MESSAGING: "emp-admin-messaging",
  ADMIN_RECOGNITION: "emp-admin-recognition",
  ADMIN_GIFTCARDS: "emp-admin-giftcards",
  ADMIN_LIBRARY: "emp-admin-library",
  ADMIN_BENEFITS: "emp-admin-benefits",
  MANAGE_SCOPE: "emp-manage-scope",
  MANAGE_CONTENT: "emp-manage-content",
  MANAGE_REPORTS: "emp-manage-reports",
  MANAGE_ACKS: "emp-manage-acks",
  MANAGE_BROADCASTS: "emp-manage-broadcasts",
  // The employee-facing pages, in the admin view.
  REQUESTS: "emp-requests",
  MESSAGES: "emp-messages",
  DIRECTORY: "emp-directory",
  REWARDS: "emp-rewards",
  LEADERBOARD: "emp-leaderboard",
  SURVEYS: "emp-surveys",
  RESOURCES: "emp-resources",
  BENEFITS: "emp-benefits",
  MY_HOME: "emp-my-home",
  MY_REQUESTS: "emp-my-requests",
  MY_MESSAGES: "emp-my-messages",
  MY_DIRECTORY: "emp-my-directory",
  MY_REWARDS: "emp-my-rewards",
  MY_LEADERBOARD: "emp-my-leaderboard",
  MY_SURVEYS: "emp-my-surveys",
  MY_RESOURCES: "emp-my-resources",
  MY_BENEFITS: "emp-my-benefits",
} as const;

export type EmployeeNavId = (typeof EMPLOYEE_NAV_IDS)[keyof typeof EMPLOYEE_NAV_IDS];

/** Counts shown on the menu items. */
interface EmployeeNavCounts {
  resourcesToAcknowledge?: number;
  /** Open surveys I haven't answered. */
  surveys?: number;
  /** Unread messages across my conversations, archived ones aside (#1346). */
  messages?: number;
  /** Open reports the caller may act on: posts and comments, and messages. */
  reports?: number;
}

function countBadge(n = 0): { badge?: number | string } {
  return n > 0 ? { badge: n > 99 ? "99+" : n } : {};
}

/**
 * Left-nav for the skyEmployee admin view, built from the caller's
 * capabilities (never from role names). It opens with the pages everyone uses,
 * so Admins and Managers never switch views to use them, then Manage and
 * Administration. A section appears only once it has an item.
 */
export function buildEmployeeNavSections(
  can: (cap: EmpCapability) => boolean,
  counts: EmployeeNavCounts = {},
  /** Whether Who can do what gives them anything to do with broadcasts. */
  broadcasts = false,
  /** Messages between colleagues is on (Messaging & broadcasts → Chats). */
  messages = true,
): NavSection[] {
  const sections: NavSection[] = [
    {
      items: [
        { id: EMPLOYEE_NAV_IDS.HOME, label: "Home", icon: House },
        { id: EMPLOYEE_NAV_IDS.REQUESTS, label: "My requests", icon: Inbox },
        {
          id: EMPLOYEE_NAV_IDS.SURVEYS,
          label: "Surveys",
          icon: ClipboardPen,
          ...countBadge(counts.surveys),
        },
        ...(messages
          ? [
              {
                id: EMPLOYEE_NAV_IDS.MESSAGES,
                label: "Messages",
                icon: MessageSquare,
                ...countBadge(counts.messages),
              },
            ]
          : []),
        { id: EMPLOYEE_NAV_IDS.DIRECTORY, label: "Directory", icon: Contact },
        // Giving and handing over rewards are on its Rewards tab, for those who may.
        { id: EMPLOYEE_NAV_IDS.REWARDS, label: "Kudos", icon: Gift },
        { id: EMPLOYEE_NAV_IDS.LEADERBOARD, label: "Leaderboard", icon: Trophy },
        {
          id: EMPLOYEE_NAV_IDS.RESOURCES,
          label: "Resources",
          icon: BookOpen,
          ...countBadge(counts.resourcesToAcknowledge),
        },
        { id: EMPLOYEE_NAV_IDS.BENEFITS, label: "Benefits", icon: HeartHandshake },
      ],
    },
  ];

  const manage: NavItem[] = [];
  // Content holds announcements, surveys, polls and banners: anyone who does
  // any of them. Deliberately in Manage, not Administration: Controllers run
  // polls and read results for their scope.
  if (
    can("content.publish") ||
    can("banners.manage") ||
    can("polls.create") ||
    can("surveys.build") ||
    can("surveys.results")
  ) {
    manage.push({ id: EMPLOYEE_NAV_IDS.MANAGE_CONTENT, label: "Content", icon: LayoutList });
  }
  // Everything reported, in one place: posts and comments for moderators,
  // messages for Admins only. The page shows each its own tab.
  if (can("content.moderate") || can("messages.reviewReports")) {
    manage.push({
      id: EMPLOYEE_NAV_IDS.MANAGE_REPORTS,
      label: "Reports",
      icon: Flag,
      ...countBadge(counts.reports),
    });
  }
  if (broadcasts) {
    manage.push({ id: EMPLOYEE_NAV_IDS.MANAGE_BROADCASTS, label: "Broadcasts", icon: Megaphone });
  }
  // Acknowledgements live inside the Resource library for whoever manages it,
  // beside the documents they are about. Managers don't have that page, so
  // for them it stays a section of its own here rather than disappearing.
  if (can("acks.track") && !can("library.manage")) {
    manage.push({
      id: EMPLOYEE_NAV_IDS.MANAGE_ACKS,
      label: "Acknowledgements",
      icon: ClipboardCheck,
    });
  }
  if (can("nav.manage")) {
    manage.push({ id: EMPLOYEE_NAV_IDS.MANAGE_SCOPE, label: "My locations & teams", icon: MapPin });
  }
  if (manage.length > 0) sections.push({ header: "Manage", items: manage });

  const admin: NavItem[] = [];
  if (can("people.manage")) {
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_USERS, label: "Users & access", icon: UserCog });
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_ORGANISATION, label: "Organisation", icon: Building2 });
  }
  if (can("recognition.admin")) {
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_RECOGNITION, label: "Recognition", icon: Award });
  }
  if (can("giftcards.admin")) {
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_GIFTCARDS, label: "Gift cards", icon: CreditCard });
  }
  if (can("benefits.manage")) {
    admin.push({
      id: EMPLOYEE_NAV_IDS.ADMIN_BENEFITS,
      label: "Benefit plans",
      icon: HeartHandshake,
    });
  }
  if (can("library.manage")) {
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_LIBRARY, label: "Resource library", icon: Library });
  }
  if (can("settings.manage")) {
    admin.push({
      id: EMPLOYEE_NAV_IDS.ADMIN_MESSAGING,
      label: "Messaging & broadcasts",
      icon: MessagesSquare,
    });
  }
  if (can("settings.manage")) {
    admin.push({ id: EMPLOYEE_NAV_IDS.ADMIN_SETTINGS, label: "Settings", icon: Settings });
  }
  if (can("nav.admin") && admin.length > 0) {
    sections.push({ header: "Administration", items: admin });
  }

  return sections;
}

/**
 * Left-nav for skyEmployee's employee-facing view (Portal View → User Portal).
 * The same for everyone: it is what an employee sees, whatever the caller's
 * own role. The profile is reached from the header's View profile, not here.
 * Resources shows how many policies are waiting for my acknowledgement.
 */
export function buildEmployeeUserNavSections(
  counts: EmployeeNavCounts = {},
  /** Messages between colleagues is on (Messaging & broadcasts → Chats). */
  messages = true,
): NavSection[] {
  return [
    {
      items: [
        { id: EMPLOYEE_NAV_IDS.MY_HOME, label: "Home", icon: House },
        { id: EMPLOYEE_NAV_IDS.MY_REQUESTS, label: "My requests", icon: Inbox },
        {
          id: EMPLOYEE_NAV_IDS.MY_SURVEYS,
          label: "Surveys",
          icon: ClipboardPen,
          ...countBadge(counts.surveys),
        },
        ...(messages
          ? [
              {
                id: EMPLOYEE_NAV_IDS.MY_MESSAGES,
                label: "Messages",
                icon: MessageSquare,
                ...countBadge(counts.messages),
              },
            ]
          : []),
        { id: EMPLOYEE_NAV_IDS.MY_DIRECTORY, label: "Directory", icon: Contact },
        { id: EMPLOYEE_NAV_IDS.MY_REWARDS, label: "Kudos", icon: Gift },
        { id: EMPLOYEE_NAV_IDS.MY_LEADERBOARD, label: "Leaderboard", icon: Trophy },
        {
          id: EMPLOYEE_NAV_IDS.MY_RESOURCES,
          label: "Resources",
          icon: BookOpen,
          ...countBadge(counts.resourcesToAcknowledge),
        },
        { id: EMPLOYEE_NAV_IDS.MY_BENEFITS, label: "Benefits", icon: HeartHandshake },
      ],
    },
  ];
}
